import { useState } from "react";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Trash2, Plus, Database, AlertTriangle } from "lucide-react";
import {
  useAdminBaselines,
  useCreateBaseline,
  useDeleteBaseline,
} from "@/hooks/useAdminBaselines";
import apiClient from "@/lib/api-client";

export const Route = createFileRoute("/admin/maintenance")({
  component: AdminMaintenancePage,
});

const EVENT_TYPES = [
  { key: "usage", label: "Usage Events" },
  { key: "engagement", label: "Engagement Events" },
  { key: "pdf-retrieval", label: "PDF Retrieval Events" },
  { key: "feedback", label: "Feedback Events" },
] as const;

function useResetEvents() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventType: string) =>
      apiClient
        .delete(`api/admin/events/${eventType}`, {
          searchParams: { confirm: "true" },
        })
        .json<{ deleted_count: number }>(),
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin"] }),
  });
}

function useResetUserEvents() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiClient
        .delete(`api/admin/users/${userId}/events`, {
          searchParams: { confirm: "true" },
        })
        .json<{ deleted_count: number }>(),
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin"] }),
  });
}

function AdminMaintenancePage() {
  const router = useRouter();
  const { data: baselines, isLoading: baselinesLoading } = useAdminBaselines();
  const createBaseline = useCreateBaseline();
  const deleteBaseline = useDeleteBaseline();
  const resetEvents = useResetEvents();
  const resetUserEvents = useResetUserEvents();

  const [newBaselineName, setNewBaselineName] = useState("");
  const [resetUserId, setResetUserId] = useState("");

  function handleCreateBaseline() {
    const name = newBaselineName.trim();
    if (!name) return;
    createBaseline.mutate({ name }, { onSuccess: () => setNewBaselineName("") });
  }

  function handleDeleteBaseline(id: string, name: string) {
    if (!window.confirm(`Delete baseline "${name}"? This cannot be undone.`)) {
      return;
    }
    deleteBaseline.mutate(id);
  }

  function handleResetEventType(key: string, label: string) {
    if (
      !window.confirm(
        `Delete ALL ${label}? This is permanent and cannot be undone.`,
      )
    ) {
      return;
    }
    resetEvents.mutate(key);
  }

  function handleResetUserEvents() {
    const userId = resetUserId.trim();
    if (!userId) return;
    if (
      !window.confirm(
        `Delete ALL events for user ${userId}? This cannot be undone.`,
      )
    ) {
      return;
    }
    resetUserEvents.mutate(userId, { onSuccess: () => setResetUserId("") });
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => router.history.back()}
        >
          <ArrowLeft className="size-4" />
        </Button>
        <h1 className="text-2xl font-bold">Maintenance</h1>
      </div>

      {/* Baselines Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="size-4" />
            Baselines
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Create form */}
          <div className="flex gap-2">
            <input
              type="text"
              value={newBaselineName}
              onChange={(e) => setNewBaselineName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreateBaseline()}
              placeholder="Baseline name..."
              className="flex-1 rounded border bg-background px-3 py-2 text-sm"
            />
            <Button
              size="sm"
              onClick={handleCreateBaseline}
              disabled={!newBaselineName.trim() || createBaseline.isPending}
            >
              <Plus className="size-4" />
              Create
            </Button>
          </div>

          {/* Baselines table */}
          {baselinesLoading && (
            <p className="text-sm text-muted-foreground">
              Loading baselines...
            </p>
          )}
          {baselines && baselines.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No baselines yet.
            </p>
          )}
          {baselines && baselines.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 font-medium">Name</th>
                    <th className="pb-2 font-medium">Scope</th>
                    <th className="pb-2 font-medium">Created At</th>
                    <th className="pb-2 font-medium w-20">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {baselines.map((b) => (
                    <tr key={b.id} className="border-b last:border-0">
                      <td className="py-2 font-medium">{b.name}</td>
                      <td className="py-2 text-muted-foreground capitalize">
                        {b.scope}
                      </td>
                      <td className="py-2 text-muted-foreground">
                        {new Date(b.created_at).toLocaleString()}
                      </td>
                      <td className="py-2">
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          onClick={() => handleDeleteBaseline(b.id, b.name)}
                          disabled={deleteBaseline.isPending}
                        >
                          <Trash2 className="size-3 text-destructive" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Event Reset Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="size-4 text-destructive" />
            Event Reset
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Permanently delete event data. These actions cannot be undone.
          </p>

          <div className="grid gap-3 sm:grid-cols-2">
            {EVENT_TYPES.map(({ key, label }) => (
              <div
                key={key}
                className="flex items-center justify-between rounded-lg border p-3"
              >
                <div>
                  <div className="text-sm font-medium">{label}</div>
                  <div className="text-xs text-muted-foreground">{key}</div>
                </div>
                <Button
                  variant="destructive"
                  size="xs"
                  onClick={() => handleResetEventType(key, label)}
                  disabled={resetEvents.isPending}
                >
                  Reset
                </Button>
              </div>
            ))}
          </div>

          {/* User events reset */}
          <div className="border-t pt-4">
            <div className="text-sm font-medium mb-2">
              Reset All Events for a User
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={resetUserId}
                onChange={(e) => setResetUserId(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleResetUserEvents()}
                placeholder="User ID..."
                className="flex-1 rounded border bg-background px-3 py-2 text-sm font-mono"
              />
              <Button
                variant="destructive"
                size="sm"
                onClick={handleResetUserEvents}
                disabled={!resetUserId.trim() || resetUserEvents.isPending}
              >
                Reset User Events
              </Button>
            </div>
          </div>

          {/* Status messages */}
          {resetEvents.isSuccess && (
            <p className="text-sm text-green-600">
              Deleted {(resetEvents.data as { deleted_count: number }).deleted_count} events.
            </p>
          )}
          {resetUserEvents.isSuccess && (
            <p className="text-sm text-green-600">
              Deleted {(resetUserEvents.data as { deleted_count: number }).deleted_count} events for user.
            </p>
          )}
          {(resetEvents.isError || resetUserEvents.isError) && (
            <p className="text-sm text-destructive">
              Reset failed. Check user ID and try again.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
